import React, { useContext } from "react";
import { UI } from "@wwimmo/ui";
import { Ticket } from "src/stores/TicketStore";
import { format } from "date-fns";
import { Trans, useTranslation } from "react-i18next";
import styles from "./TicketListRow.module.css";
import { ColorStyle } from "src/utils/Colors";
import { getIcon } from "src/screens/tickets/Tickets";
import { RootStoreContext } from "src/stores/RootStore";
import { SCREEN_SIZE } from "src/stores/UIStore";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";

const TicketListRowDesktopBase = (props: { ticket: Ticket; widths: number[] }) => {
    const { uiStore } = useContext(RootStoreContext);

    const {
        number,
        date,
        workflow_type,
        appliance,
        incidentContactPerson: person,
        state,
        realestate,
        assignedUser,
        title,
        orderListnumbers,
        hasNewMessage
    } = props.ticket;

    const { widths } = props;
    const { t } = useTranslation();

    const assignedUserName = `${assignedUser?.name1 || ""} ${assignedUser?.name2 || ""}`.trim();

    const displayOrderListnumbers = orderListnumbers
        .slice(0, 3) // Get only the first three items, if available
        .map((item) => item)
        .join(", ");

    return (
        <UI.List.Row className={styles.TicketListRow} key={number}>
            <UI.List.Cell
                className={styles.NewMessageCell}
                colspan={widths[0]}
                value={
                    hasNewMessage && (
                        <span className={styles.NewMessageDot} title={t("screens.tickets.list.new_message")} />
                    )
                }
            />
            <UI.List.Cell
                className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP_L ? "d-none" : "d-flex"}`}
                value={
                    <div className={styles.TypeIcon}>
                        <img
                            src={getIcon(workflow_type)}
                            alt={t(`easyticket.workflow_types.${String(workflow_type)}`) ?? "icon"}
                        />
                    </div>
                }
                colspan={widths[1]}
            />
            <UI.List.Cell value={number} colspan={widths[2]} />
            <UI.List.Cell
                value={
                    uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP
                        ? Ticket.formatDate(date ?? format(new Date(), "d.M.yy"))
                        : Ticket.formatDate(date ?? format(new Date(), "d.M.yy"), true)
                }
                colspan={widths[3]}
            />
            <UI.List.Cell
                colspan={widths[4]}
                value={
                    <>
                        <strong>
                            {title ? title : <Trans>easyticket.workflow_types.{String(workflow_type)}</Trans>}
                        </strong>
                        {appliance && (
                            <>
                                <br />
                                <span>{appliance.name}</span>
                            </>
                        )}
                    </>
                }
                truncate
            />
            <UI.List.Cell
                colspan={widths[5]}
                value={
                    realestate && (
                        <div>
                            <div>{realestate.name}</div>
                            <div>
                                {realestate.zip} {realestate.city}
                            </div>
                        </div>
                    )
                }
                truncate
            />
            <UI.List.Cell
                className={`${uiStore.currentScreenSize < SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                colspan={widths[6]}
                value={
                    person && (
                        <div>
                            <div>
                                {person.name1} {person.name2 ?? ""}
                            </div>
                            {person.email && <div>{person.email}</div>}
                            {person.mobile && <div>{person.mobile}</div>}
                        </div>
                    )
                }
                truncate
            />
            <UI.List.Cell
                value={
                    <div className={styles.CellContainerCenter}>
                        {uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP ? (
                            <UI.Icon
                                icon={UI.SVGIcon.Profile}
                                color={ColorStyle(assignedUserName ? "primary" : "yellow")}
                            />
                        ) : undefined}
                        <span className={styles.AssignedName} title={assignedUserName}>
                            {assignedUserName || "-"}
                        </span>
                    </div>
                }
                colspan={widths[7]}
            />
            <UI.List.Cell
                className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                colspan={widths[8]}
                value={
                    <div className={styles[`status-${displayOrderListnumbers}`]}>
                        {displayOrderListnumbers}
                        {orderListnumbers.length > 3 && (
                            <>
                                , <span className={styles.Dots}>...</span>
                            </>
                        )}
                    </div>
                }
                truncate
            />
            <UI.List.Cell
                className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                colspan={widths[9]}
                value={
                    <div className={styles[`status-${state}`]}>
                        <Trans>easyticket.states.{state}</Trans>
                    </div>
                }
                truncate
            />
        </UI.List.Row>
    );
};

export const TicketListRowDesktop = Sentry.withProfiler(observer(TicketListRowDesktopBase));
